import { useEffect, useRef, useState, type ReactNode } from "react";
import { TerminalSquare, CircleCheck } from "lucide-react";
import { useOnboarding } from "@/hooks/use-onboarding";
import { cn } from "@/lib/utils";
import type { Platform } from "@/stores/chat-store";
import type { HelpStepComponentProps } from "../help-flows";
import { useScrollSpotlight } from "../scroll-spotlight";

interface PlatformOption {
  id: Platform;
  label: string;
  subtitle: string;
  icon: ReactNode;
  accentClass: string;
  borderClass: string;
}

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "mac";
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("win")) return "windows-powershell";
  return "mac";
}

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const WindowsIcon = () => (
  <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor">
    <path d="M3 12V6.75l6-1.32v6.48L3 12zm17-9v8.75l-10 .08V5.21L20 3zm-10 9.32l10-.1V21l-10-1.76V12.32zM3 12.5l6 .09v6.81l-6-1.01V12.5z" />
  </svg>
);



const PLATFORMS: PlatformOption[] = [
  {
    id: "mac",
    label: "macOS",
    subtitle: "Use this if you are on a Mac.",
    icon: <AppleIcon />,
    accentClass: "bg-violet-500/15 text-violet-300",
    borderClass: "border-violet-500/30",
  },
  {
    id: "windows-powershell",
    label: "Windows",
    subtitle: "Use this if you are on a Windows PC.",
    icon: <WindowsIcon />,
    accentClass: "bg-blue-500/15 text-blue-300",
    borderClass: "border-blue-500/30",
  },
];

/* -------------------------------------------------------------------------- */
/*  Terminal how-to card — appears below the selected platform                */
/* -------------------------------------------------------------------------- */

interface TerminalStep {
  action: string;
  detail: string;
}

function getTerminalSteps(platform: Platform): { title: string; app: string; steps: TerminalStep[] } {
  if (platform === "mac") {
    return {
      title: "Open Terminal on your Mac",
      app: "Terminal",
      steps: [
        { action: "Open Spotlight", detail: "Press ⌘ + Space." },
        { action: 'Type "Terminal"', detail: "You should see the Terminal app pop up." },
        { action: "Press Enter", detail: "A Terminal window opens. This is where you will paste setup commands." },
      ],
    };
  }

  const app = platform === "windows-cmd" ? "Command Prompt" : "PowerShell";
  const search = platform === "windows-cmd" ? "cmd" : "PowerShell";
  return {
    title: `Open ${app} on Windows`,
    app,
    steps: [
      { action: "Open Start", detail: "Press the Windows key or click Start." },
      { action: `Type "${search}"`, detail: `You should see ${app} show up in the list.` },
      { action: "Open the app", detail: `Press Enter. If Windows asks for permission, click Yes so setup can finish without permission problems.` },
    ],
  };
}

function TerminalHowTo({ platform }: { platform: Platform }) {
  const { title, steps } = getTerminalSteps(platform);

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-violet-500/10 text-violet-400">
          <TerminalSquare className="h-5 w-5" />
        </div>
        <div>
          <p className="text-base font-semibold text-foreground">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            This is the window where you will paste the setup commands.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {steps.map((step, index) => (
          <div key={index} className="flex gap-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-xs font-semibold text-foreground">
              {index + 1}
            </div>
            <div className="min-w-0 pt-0.5">
              <p className="text-sm font-semibold text-foreground">{step.action}</p>
              <p className="mt-0.5 text-sm leading-6 text-muted-foreground">{step.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PlatformSelectStep                                                        */
/* -------------------------------------------------------------------------- */

export function PlatformSelectStep({ onAdvance }: Pick<HelpStepComponentProps, "onAdvance">) {
  const { selectedPlatform, setSelectedPlatform } = useOnboarding();
  const [detected] = useState<Platform>(detectPlatform);
  const [chosen, setChosen] = useState<Platform>(selectedPlatform ?? detected);
  const [showShellPicker, setShowShellPicker] = useState(
    chosen === "windows-powershell" || chosen === "windows-cmd"
  );
  const gridRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const howtoRef = useRef<HTMLDivElement>(null);
  useScrollSpotlight(gridRef);
  useScrollSpotlight(shellRef);
  useScrollSpotlight(howtoRef);

  useEffect(() => {
    setSelectedPlatform(chosen);
  }, [chosen, setSelectedPlatform]);

  const handlePlatformClick = (platform: Platform) => {
    if (
      platform === chosen ||
      (platform === "windows-powershell" && (chosen === "windows-powershell" || chosen === "windows-cmd"))
    ) {
      onAdvance?.();
      return;
    }

    if (platform === "windows-powershell") {
      setShowShellPicker(true);
      setChosen(chosen === "windows-cmd" ? "windows-cmd" : "windows-powershell");
      return;
    }

    setShowShellPicker(false);
    setChosen(platform);
  };

  const handleShellClick = (platform: Extract<Platform, "windows-powershell" | "windows-cmd">) => {
    if (platform === chosen) {
      onAdvance?.();
      return;
    }

    setChosen(platform);
  };

  const isDetected = (platform: Platform) => {
    if (platform === "windows-powershell") {
      return detected.startsWith("windows");
    }

    return detected === platform;
  };

  return (
    <div className="space-y-5">
      <div ref={gridRef} className="grid gap-3 md:grid-cols-2">
        {PLATFORMS.map((platform) => {
          const active =
            platform.id === chosen ||
            (platform.id === "windows-powershell" && chosen === "windows-cmd");

          return (
            <button
              key={platform.id}
              type="button"
              onClick={() => {
                handlePlatformClick(platform.id);
              }}
              className={cn(
                "group min-h-[200px] rounded-xl border bg-white/[0.03] p-5 text-left transition-all hover:border-white/[0.1] hover:bg-white/[0.05]",
                active ? `${platform.borderClass} bg-white/[0.05] ring-1 ring-white/[0.08]` : "border-white/[0.06]"
              )}
            >
              <div className="flex h-full flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className={cn("inline-flex rounded-xl border border-white/[0.08] p-3", platform.accentClass)}>
                    {platform.icon}
                  </div>
                  {active ? (
                    <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-foreground/80">
                      Selected
                    </span>
                  ) : null}
                </div>
                <div className="mt-5 space-y-1.5">
                  <p className="text-base font-semibold text-foreground">{platform.label}</p>
                  <p className="text-sm leading-6 text-muted-foreground">{platform.subtitle}</p>
                </div>
                {active && isDetected(platform.id) ? (
                  <p className="mt-auto pt-4 flex items-center gap-1.5 text-xs font-medium text-emerald-400"><CircleCheck className="h-3.5 w-3.5" /> Detected on this device</p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      {showShellPicker ? (
        <div ref={shellRef} className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
          <div className="mb-4 space-y-1">
            <p className="text-sm font-semibold text-foreground">Choose your Windows shell</p>
            <p className="text-sm leading-6 text-muted-foreground">
              Pick the window you want to use. Click the same choice again when you are ready to move on.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                handleShellClick("windows-powershell");
              }}
              className={cn(
                "rounded-xl border bg-white/[0.03] p-4 text-left transition-all hover:border-white/[0.1] hover:bg-white/[0.05]",
                chosen === "windows-powershell"
                  ? "border-blue-500/30 bg-white/[0.05] ring-1 ring-white/[0.08]"
                  : "border-white/[0.06]"
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-foreground">PowerShell</p>
                <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-300">
                  Recommended
                </span>
              </div>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                The newer Windows terminal. It is the easiest choice for most people.
              </p>
            </button>
            <button
              type="button"
              onClick={() => {
                handleShellClick("windows-cmd");
              }}
              className={cn(
                "rounded-xl border bg-white/[0.03] p-4 text-left transition-all hover:border-white/[0.1] hover:bg-white/[0.05]",
                chosen === "windows-cmd"
                  ? "border-blue-500/30 bg-white/[0.05] ring-1 ring-white/[0.08]"
                  : "border-white/[0.06]"
              )}
            >
              <p className="text-sm font-semibold text-foreground">Command Prompt</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                The older Windows terminal. Choose this if you already use Command Prompt.
              </p>
            </button>
          </div>
        </div>
      ) : null}

      {/* Terminal how-to card — shown below selected platform */}
      <div ref={howtoRef}>
        <TerminalHowTo platform={chosen} />
      </div>
    </div>
  );
}
